import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Mail, ArrowLeft, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Page where a patient or caregiver requests a magic-link to update their
 * profile. We never reveal whether their email is on file or not — the
 * success state is shown for every submission to prevent email-enumeration.
 */
export default function AccountRequestLinkPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [doneMessage, setDoneMessage] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/account/request-link`, {
        email: trimmed,
      });
      setDoneMessage(data.message);
      setDone(true);
    } catch {
      // Backend always returns 200 by design; show generic confirmation anyway
      setDoneMessage(
        "If your email is registered with MediTalk, we've sent you a secure link. Please check your inbox in a few minutes.",
      );
      setDone(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white min-h-screen">
      {/* Hero — DARK */}
      <section className="bg-[var(--dark-bg)] grain-dark relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute -top-32 -left-24 w-[420px] h-[420px] rounded-full bg-[var(--lime)]/12 blur-3xl pointer-events-none"
        />
        <div className="container-main py-16 md:py-20 relative z-10">
          <Link
            to="/register"
            className="inline-flex items-center gap-1.5 text-sm text-white/80 hover:text-white transition-colors mb-6"
            data-testid="account-back-link"
          >
            <ArrowLeft size={14} />
            Back to Register
          </Link>
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-[var(--lime)] mb-4">
              <span className="w-8 h-px bg-[var(--lime)]" />
              Update your details
            </span>
            <h1 className="font-['Outfit'] font-bold text-white tracking-tight text-4xl md:text-5xl mb-5 leading-[1.05]">
              Already registered?
              <span className="text-[var(--lime)]"> Update what we know.</span>
            </h1>
            <p className="text-lg text-[var(--gray-light)] leading-relaxed max-w-xl">
              Enter the email you signed up with. We'll send you a one-time
              secure link to review and update your details. No password to
              remember.
            </p>
          </div>
        </div>
      </section>

      {/* Form — LIGHT */}
      <section className="py-16 md:py-24">
        <div className="container-main max-w-xl">
          {done ? (
            <div
              className="rounded-3xl bg-[var(--bone-soft)] border border-[var(--lime)]/40 p-8 md:p-10 text-center"
              data-testid="account-link-sent"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[var(--lime)] mb-6">
                <Check
                  size={30}
                  strokeWidth={3}
                  className="text-[var(--dark-bg)]"
                />
              </div>
              <h2 className="font-['Outfit'] font-bold text-[var(--charcoal)] text-2xl md:text-3xl tracking-tight mb-4 leading-tight">
                Check your inbox
              </h2>
              <p className="text-[var(--text-muted)] leading-relaxed mb-8 max-w-md mx-auto">
                {doneMessage}
              </p>
              <p className="text-xs text-[var(--text-muted)] leading-relaxed">
                The link is single-use and expires in 24 hours. If you don't
                see it, check your spam folder or{" "}
                <button
                  type="button"
                  className="text-[var(--charcoal)] font-semibold underline decoration-[var(--lime)] decoration-2 underline-offset-4"
                  onClick={() => {
                    setDone(false);
                    setEmail("");
                  }}
                >
                  try again
                </button>
                .
              </p>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              className="rounded-3xl bg-[var(--bone-soft)] border border-[var(--light-border)] p-8 md:p-10"
              data-testid="account-link-form"
            >
              <label
                htmlFor="account-email"
                className="block text-[15px] font-semibold text-[var(--charcoal)] mb-2"
              >
                Your email address
              </label>
              <div className="relative">
                <Mail
                  size={16}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-muted)]"
                />
                <input
                  id="account-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full h-12 pl-11 pr-4 rounded-xl border border-[var(--light-border)] bg-white text-[15px] text-[var(--charcoal)] placeholder:text-[var(--text-muted)]/60 transition-all focus:outline-none focus:border-[var(--lime)] focus:ring-4 focus:ring-[var(--lime)]/20"
                  data-testid="account-email-input"
                  autoComplete="email"
                  autoFocus
                />
              </div>
              <p className="text-[12.5px] text-[#475569] mt-2 leading-relaxed">
                Use the same email you registered with. We'll send a one-time
                secure link to that address.
              </p>
              <button
                type="submit"
                disabled={submitting}
                className="mt-7 w-full md:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[var(--lime)] text-[var(--dark-bg)] font-semibold text-sm hover:bg-[var(--lime-dark)] transition-all disabled:opacity-60 disabled:cursor-not-allowed shadow-[0_8px_22px_-10px_rgba(196,247,75,0.6)]"
                data-testid="account-link-submit"
              >
                {submitting ? "Sending..." : "Email me a secure link"}
              </button>
            </form>
          )}

          <p className="text-center text-sm text-[var(--text-muted)] mt-10">
            Not registered yet?{" "}
            <Link
              to="/register"
              className="text-[var(--charcoal)] font-semibold underline decoration-[var(--lime)] decoration-2 underline-offset-4"
            >
              Register here
            </Link>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
